/**
 * Gift Guide Quiz Engine v2
 * Self-contained custom element for personalized jewelry recommendations.
 * Architecture: HTMLElement, innerHTML rendering, CSS class animations.
 * Integrations: Klaviyo client API, GA4 (future), email capture with RGPD.
 */

/* ============================================================
   KLAVIYO

   These are PUBLIC identifiers, not secrets. The company_id is the same
   site ID already present in any Klaviyo onsite snippet, and list ids are
   not sensitive. No private key is, or may be, in the theme.

   Verified against the live Klaviyo account on 2026-08-11:
     LEYGRJ  Nouvel Heritage Inc
     J85w5S  Newsletter                              (single opt-in)
     RjKi4D  Language — French (from Shopify locale) (single opt-in)
   ============================================================ */
const KLAVIYO_PUBLIC_KEY = 'LEYGRJ';
const KLAVIYO_NEWSLETTER_LIST = 'J85w5S';
const KLAVIYO_FRENCH_LIST = 'RjKi4D';
/* Klaviyo pins behaviour to a dated revision. Bump deliberately, never
   silently: an unpinned API is how a working integration breaks on a
   Tuesday for no visible reason. */
const KLAVIYO_REVISION = '2026-07-15';
/* The metric name is the flow's trigger. Changing this string silently
   detaches the live flow from the quiz, and Klaviyo will not warn you: it will
   just quietly create a second metric nobody is listening to. Treat it as
   fixed. */
const KLAVIYO_METRIC = 'Completed gift guide';

/* The two funnel metrics added 15/09/2026.

   `Completed gift guide` above is MISNAMED and cannot be renamed: it is the
   trigger for live flow UxLPSP, and it fires on the optional email submit at
   the very end, not on completion. Until these two existed we could see only
   that last optional step, so we could not say how many people started, how
   many reached their recommendations, or which entry point sent them. The
   monthly review had no completion figure to report because none was ever
   recorded.

   Read them as:
     Started gift guide          q1 rendered
     Viewed gift guide results   the personal edit shown, email or not
     Completed gift guide        email submitted (historic name, leave alone)
*/
const KLAVIYO_METRIC_STARTED = 'Started gift guide';
const KLAVIYO_METRIC_RESULTS = 'Viewed gift guide results';

/* Which placement sent this visitor. Written by the advisor consultation card
   (snippets/advisor-consultation-card.liquid) as ?entry_point=collection, and
   read once on load. Kept in sessionStorage because the quiz is a single page
   but the parameter is lost the moment anything rewrites the URL. */
const GG_ENTRY_POINT_KEY = 'nh_gg_entry_point';
const GG_RESULTS_FIRED_KEY = 'nh_gg_results_fired';

/* ============================================================
   ADVISORS

   Titles confirmed by Camille, 2026-08-11. The previous version of this
   array carried wrong titles AND a `why` line per advisor -- invented
   quotes ("This is the one I'd choose", "I keep coming back to this for
   my clients") attributed to real, named colleagues about products none
   of them had chosen. Every `why` a customer sees now comes from the
   advisor's own note on the pick, or is not shown at all.

   `bio` is used on the welcome screen only. There is deliberately no
   `why` field here: if a pick has no note, the card simply omits it.
   ============================================================ */
/* `roleKey` rather than a literal role, since 2026-09-14. The three titles
   were hardcoded English here and bypassed the section's locale dictionary
   entirely, so /fr showed SENIOR SALES / VIP SPECIALIST / FOUNDER on the one
   screen whose job is to build confidence before the consultation starts.
   Names stay as they are: Camille confirmed the advisors are named on this
   page deliberately, in both languages. */
const TEAM = {
  James: { name: 'James', roleKey: 'advisor_role_senior_sales', initial: 'J', bio: 'Guides clients to their perfect piece every day.' },
  Emily: { name: 'Emily', roleKey: 'advisor_role_vip_specialist', initial: 'E', bio: 'Specializes in finding the unexpected match.' },
  Camille: { name: 'Camille', roleKey: 'advisor_role_founder', initial: 'C', bio: 'Designs every collection with a story in mind.' },
};
/* Welcome-screen order. Antoine is deliberately absent: he has no picks and
   no confirmed title (open question for Camille, 2026-08-11). */
const TEAM_ORDER = ['James', 'Emily', 'Camille'];

/* ============================================================
   SCENARIO TABLE

   The 40 scenarios the team wrote, normalized into scoring dimensions.
   Source of truth: Gift Guide Quiz/gift-guide-scenarios.json in the
   Cowork workspace, itself derived from the team's own grid.

   `track` is a hard filter. `occasion`, `owns`, `style` and `budget` are
   populated on all 40, so they are the strong signals; the rest are
   sparse and weighted lower.
   ============================================================ */
const SCENARIOS = [
  { code: 'S01', track: 'self', dims: {"occasion":"milestone","owns":"mood","intent":"complete","style":"statement","budget":"mid"} },
  { code: 'S02', track: 'self', dims: {"occasion":"birthday","owns":"first","style":"minimal","budget":"entry","sizes":"none"} },
  { code: 'S03', track: 'self', dims: {"occasion":"milestone","owns":"first","style":"statement","budget":"high","daily":"watch","sizes":"ring"} },
  { code: 'S04', track: 'self', dims: {"occasion":"collection","owns":"mood","intent":"stack","style":"layering","budget":"premium"} },
  { code: 'S05', track: 'self', dims: {"occasion":"collection","owns":"other_brands","style":"layering","budget":"mid","daily":"necklaces"} },
  { code: 'S06', track: 'self', dims: {"occasion":"birthday","owns":"tryme","intent":"complete","style":"statement","budget":"mid"} },
  { code: 'S07', track: 'self', dims: {"occasion":"milestone","owns":"first","style":"everyday","budget":"entry","daily":"watch","sizes":"ring"} },
  { code: 'S08', track: 'self', dims: {"occasion":"milestone","owns":"latch","intent":"different","style":"minimal","budget":"mid","sizes":"none"} },
  { code: 'S09', track: 'self', dims: {"occasion":"collection","owns":"first","style":"minimal","budget":"premium","daily":"bracelets","sizes":"both"} },
  { code: 'S10', track: 'self', dims: {"occasion":"birthday","owns":"other_brands","style":"layering","budget":"entry","daily":"earrings","sizes":"ring"} },
  { code: 'S11', track: 'self', dims: {"occasion":"milestone","owns":"mood","intent":"stack","style":"statement","budget":"high","diamonds":"heavy"} },
  { code: 'S12', track: 'self', dims: {"occasion":"birthday","owns":"other_brands","style":"everyday","budget":"mid","daily":"earrings","diamonds":"accent"} },
  { code: 'S13', track: 'self', dims: {"occasion":"collection","owns":"other_brands","style":"statement","budget":"high","daily":"watch"} },
  { code: 'S14', track: 'self', dims: {"occasion":"milestone","owns":"first","style":"layering","budget":"mid","sizes":"none","diamonds":"accent"} },
  { code: 'S15', track: 'self', dims: {"occasion":"collection","owns":"tryme","intent":"stack","style":"layering","budget":"premium"} },
  { code: 'S16', track: 'self', dims: {"occasion":"birthday","owns":"first","style":"statement","budget":"mid","daily":"necklaces","sizes":"ring"} },
  { code: 'H01', track: 'her', dims: {"occasion":"anniversary","owns":"mood","intent":"complete","style":"statement","budget":"premium"} },
  { code: 'H02', track: 'her', dims: {"occasion":"birthday","owns":"first","style":"minimal","budget":"high","daily":"necklaces","sizes":"none"} },
  { code: 'H03', track: 'her', dims: {"occasion":"anniversary","owns":"first","style":"bold","budget":"premium","daily":"none","sizes":"none"} },
  { code: 'H04', track: 'her', dims: {"occasion":"collection","owns":"mood","intent":"stack","style":"layering","budget":"high"} },
  { code: 'H05', track: 'her', dims: {"occasion":"birthday","owns":"other_brands","style":"layering","budget":"mid","daily":"earrings","sizes":"ring"} },
  { code: 'H06', track: 'her', dims: {"occasion":"milestone","owns":"tryme","intent":"complete","style":"everyday","budget":"mid"} },
  { code: 'H07', track: 'her', dims: {"occasion":"birthday","owns":"first","style":"everyday","budget":"entry","daily":"watch","sizes":"none"} },
  { code: 'H08', track: 'her', dims: {"occasion":"anniversary","owns":"mood","intent":"different","style":"minimal","budget":"high"} },
  { code: 'H09', track: 'her', dims: {"occasion":"milestone","owns":"first","style":"statement","budget":"mid","sizes":"none","diamonds":"accent"} },
  { code: 'H10', track: 'her', dims: {"occasion":"birthday","owns":"latch","intent":"complete","style":"minimal","budget":"mid","daily":"bracelets"} },
  { code: 'H11', track: 'her', dims: {"occasion":"milestone","owns":"other_brands","style":"bold","budget":"premium","daily":"earrings"} },
  { code: 'H12', track: 'her', dims: {"occasion":"collection","owns":"first","style":"layering","budget":"entry","sizes":"ring"} },
  { code: 'H13', track: 'her', dims: {"occasion":"anniversary","owns":"mood","intent":"complete","style":"minimal","budget":"mid","diamonds":"minimal"} },
  { code: 'H14', track: 'her', dims: {"occasion":"birthday","owns":"other_brands","style":"everyday","budget":"high","daily":"watch","diamonds":"accent"} },
  { code: 'M01', track: 'him', dims: {"occasion":"milestone","owns":"daily","style":"classic","budget":"mid","daily":"rings_bracelets_watch"} },
  { code: 'M02', track: 'him', dims: {"occasion":"holiday","owns":"first","style":"minimal","budget":"entry","daily":"watch_only","sizes":"none","diamonds":"minimal"} },
  { code: 'M03', track: 'him', dims: {"occasion":"anniversary","owns":"daily","style":"bold","budget":"premium","sizes":"ring","diamonds":"heavy"} },
  { code: 'M04', track: 'him', dims: {"occasion":"birthday","owns":"first","style":"classic","budget":"mid","sizes":"ring","diamonds":"accent"} },
  { code: 'M05', track: 'him', dims: {"occasion":"milestone","owns":"minimal","style":"understated","budget":"high","daily":"watch_only","sizes":"ring"} },
  { code: 'M06', track: 'him', dims: {"occasion":"milestone","owns":"first","style":"bold","budget":"premium","sizes":"both","diamonds":"heavy"} },
  { code: 'M07', track: 'him', dims: {"occasion":"birthday","owns":"other_brands","style":"classic","budget":"mid","daily":"rings_bracelets","sizes":"both"} },
  { code: 'M08', track: 'him', dims: {"occasion":"holiday","owns":"daily","style":"classic","budget":"high","daily":"necklaces_rings","sizes":"ring","diamonds":"accent"} },
  { code: 'M09', track: 'him', dims: {"occasion":"milestone","owns":"minimal","style":"understated","budget":"entry","daily":"watch_only","sizes":"none","diamonds":"minimal"} },
  { code: 'M10', track: 'him', dims: {"occasion":"anniversary","owns":"first","style":"understated","budget":"mid","sizes":"none"} },
];

/* Read the server-rendered payloads emitted by sections/gift-guide.liquid.
   Both are optional: if the section is somehow rendered without them the
   quiz still runs and simply shows no recommendations, rather than
   throwing partway through the results render. */
function readJsonScript(id) {
  const el = document.getElementById(id);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (err) {
    console.error('[gift-guide] could not parse #' + id, err);
    return null;
  }
}

const PICKS = readJsonScript('gift-guide-picks') || [];

/* ============================================================
   PRESENTMENT CURRENCY SYMBOL

   The three budget bands on q12 are marketing thresholds written with a
   dollar sign ("Under $3,000"), so a EUR shopper was reading dollars on
   step 10 (walkthrough of 18/08/2026).

   snippets/nh-title-currency.liquid already solves exactly this for
   collection titles, and its reasoning carries over unchanged: swap the
   SYMBOL for the shopper's presentment currency symbol and leave the NUMBER
   alone. These are rounded bands chosen per market, not converted prices, so
   "Under EUR 3,000" is intended and "Under EUR 2,764" would not be. Nothing
   here implies an FX rate.

   That snippet cannot reach these particular strings, because they are
   locale strings rendered by JavaScript rather than Liquid output. So the
   symbol is read in Liquid, emitted as JSON alongside the picks payload, and
   substituted at render time by t().
   ============================================================ */
const GG_CONFIG = readJsonScript('gift-guide-config') || {};
const CURRENCY_SYMBOL = typeof GG_CONFIG.currencySymbol === 'string' && GG_CONFIG.currencySymbol
  ? GG_CONFIG.currencySymbol
  : '$';

/* Same guard as the snippet: only touch a string that actually carries a
   currency symbol. On a dollar market the swap is a no-op for the English
   bands anyway, because their symbol IS "$".

   BOTH symbols are handled, not just "$". The English bands are written
   "$3,000" and the French ones write the same three thresholds the French way,
   "3 000 € à 6 000 €". Handling only "$" would fix English on a euro market
   and leave French showing euros on a dollar market, which is the same bug
   with the languages swapped. The harness covers all four combinations.

   Those three keys are the only ones in either language containing either
   character, asserted by the key-sync check, so nothing else can be caught.

   The replacement is a FUNCTION rather than a string so that a symbol
   containing "$&" or "$1" cannot be re-read as a regexp replacement
   pattern. */
function localizeCurrency(text) {
  if (typeof text !== 'string') return text;
  if (text.indexOf('$') === -1 && text.indexOf('€') === -1) return text;
  return text.replace(/[$€]/g, () => CURRENCY_SYMBOL);
}

/* Picks grouped by scenario code, in a stable advisor order so the same
   scenario always lists advisors the same way. */
const PICKS_BY_SCENARIO = PICKS.reduce((acc, pick) => {
  if (!pick || !pick.scenario) return acc;
  (acc[pick.scenario] = acc[pick.scenario] || []).push(pick);
  return acc;
}, {});
Object.keys(PICKS_BY_SCENARIO).forEach((code) => {
  PICKS_BY_SCENARIO[code].sort((a, b) => {
    const ai = TEAM_ORDER.indexOf(a.advisor);
    const bi = TEAM_ORDER.indexOf(b.advisor);
    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
  });
});

/* ============================================================
   MATCHING ENGINE
   ============================================================ */

/* The quiz and the scenario grid were written separately and do not use
   identical vocabularies. q5 offers "selfcare" where the grid says
   "milestone", and one quiz answer ("Birthday or holiday") legitimately
   covers three grid occasions. Rather than force either side to change,
   translate here and give partial credit for a synonym. */
const OCCASION_SYNONYMS = {
  selfcare: ['milestone'],
  milestone: ['milestone'],
  holiday: ['birthday', 'holiday', 'anniversary'],
  collection: ['collection'],
};

const STYLE_FROM_Q11 = {
  layers: 'layering',
  signature: 'statement',
  everyday: 'everyday',
  minimal_style: 'minimal',
};

const DAILY_FROM_Q8 = {
  necklace: 'necklaces',
  bracelet_stack: 'bracelets',
  earrings: 'earrings',
  watch: 'watch',
  none: 'none',
};

/* Scenario `daily` values are composites like "rings_bracelets_watch".
   Normalize both sides to a Set so they can be compared by overlap. */
function dailySet(value) {
  if (!value || value === 'none' || value === 'nothing') return new Set();
  return new Set(String(value).replace('_only', '').split('_').filter(Boolean));
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
}

/* Translate the visitor's answers into the same dimensions the scenarios
   are described in. Anything the visitor was not asked stays undefined and
   simply does not score. That tolerance was built for the express path,
   which was removed on 18/08/2026, but it still earns its keep: the For Him
   branch asks eight questions rather than ten, and a visitor can skip both
   name steps. */
function answersToDims(state) {
  const a = (state && state.answers) || {};
  const track = a.q3 || 'self';
  const isHim = track === 'him';
  const dims = { track };

  dims.occasion = a.q5 || null;

  /* `owns`: the self/her path asks q6 outright. The him path splits it,
     asking q5hc only when he is building a collection and q6h always. */
  dims.owns = isHim ? (a.q6h || a.q5hc || null) : (a.q6 || null);

  dims.intent = a.q7 || null;
  dims.style = isHim ? (a.q8h || null) : (STYLE_FROM_Q11[a.q11] || null);
  dims.budget = a.q12 || null;
  dims.diamonds = a.q10 || a.q10h || null;

  if (isHim) {
    const worn = asArray(a.q7h).filter((v) => v !== 'nothing');
    dims.daily = worn.length ? worn.join('_') : 'none';
  } else {
    dims.daily = DAILY_FROM_Q8[a.q8] || null;
  }

  /* Sizes: q11b is a multi-select where "I don't know either" is mutually
     exclusive with the ring/wrist options (enforced in handleClick). */
  const sizeAnswers = asArray(a.q11b);
  const knowsRing = sizeAnswers.includes('ring_yes');
  const knowsWrist = sizeAnswers.includes('wrist_yes');
  if (sizeAnswers.length) {
    if (knowsRing && knowsWrist) dims.sizes = 'both';
    else if (knowsRing) dims.sizes = 'ring';
    else if (knowsWrist) dims.sizes = 'wrist';
    else dims.sizes = 'none';
  } else {
    dims.sizes = null;
  }

  return dims;
}

/* Weights: occasion, owns, style and budget are present on all 40
   scenarios, so they discriminate reliably. The rest are sparse. */
const DIM_WEIGHTS = {
  occasion: 5, owns: 5, style: 5, budget: 5,
  intent: 3, diamonds: 2, daily: 2, sizes: 2,
};

function scoreScenario(dims, scenario) {
  if (dims.track && scenario.track !== dims.track) return -Infinity;

  let score = 0;
  Object.keys(DIM_WEIGHTS).forEach((key) => {
    const want = dims[key];
    const have = scenario.dims[key];
    if (want == null || have == null) return;
    const weight = DIM_WEIGHTS[key];

    if (key === 'occasion') {
      const synonyms = OCCASION_SYNONYMS[want] || [want];
      if (synonyms[0] === have) score += weight;
      else if (synonyms.includes(have)) score += weight * 0.6;
      return;
    }

    if (key === 'daily') {
      const wantSet = dailySet(want);
      const haveSet = dailySet(have);
      if (!wantSet.size && !haveSet.size) { score += weight; return; }
      let shared = 0;
      wantSet.forEach((v) => { if (haveSet.has(v)) shared += 1; });
      const union = new Set([...wantSet, ...haveSet]).size;
      if (union) score += weight * (shared / union);
      return;
    }

    if (want === have) score += weight;
  });

  return score;
}

/* ---------- safety rules ----------

   These are derived from the VISITOR's own answers, not from prose, and
   they exist to keep the promises the quiz makes on screen. When someone
   says they wear a necklace every day, the quiz answers "We would
   recommend a bracelet, ring, or earrings instead"; showing them a
   necklace would break that in front of them.

   They are applied ONLY to picks borrowed from a neighbouring scenario.
   A pick an advisor made against this exact scenario is a deliberate
   human override and always wins -- that is the whole point of curation.
   Emily's Black Mini Latch Cord on M10 is the live example: the cord has
   S/M/L sizing on a "sizes unknown" scenario, and she chose it anyway. */
const BUDGET_CEILING = { entry: 3000, mid: 6000, high: 10000, premium: Infinity };

/* Sized pieces carry a "Size" option; one-size pieces carry Shopify's
   default "Title". Checked against live data 2026-08-11 across bangles,
   rings, pendants, hoops and cords. `Taille` covers the French locale. */
function needsSizing(pick) {
  return asArray(pick.optionNames).some((name) => /^(size|taille)$/i.test(String(name).trim()));
}

/* Which body measurement a sized piece actually needs. Keys are the live
   product types (checked 2026-08-11: Bracelets, Earrings, Home, Individual
   Chains, Individual Clasps, Individual Cords, Individual Pendants,
   Necklaces, Rings, Tie Pin).

   Types absent from this map are deliberately unconstrained even when they
   carry a Size option, because for them "size" is a preference rather than
   a measurement: a necklace's Size is chain length, which the customer
   chooses freely and does not need to have measured. Blocking those would
   strip good recommendations for no safety benefit.

   Individual Cords belongs here and its omission was a real bug caught by
   the engine harness: M09 (minimal lifestyle, no sizes known) fell through
   to Emily's Black Mini Latch Cord, which carries S/M/L wrist sizing. */
const SIZED_TYPES = {
  Rings: 'ring',
  Bracelets: 'wrist',
  'Individual Cords': 'wrist',
};

function safetyRulesFor(dims) {
  const blockedTypes = new Set();

  /* What they wear every day is what we said we would avoid. Bracelet
     stackers are the exception: the copy says "let's build on what's
     already there", so bracelets stay eligible for them. */
  if (dims.daily) {
    const worn = dailySet(dims.daily);
    if (worn.has('necklaces')) blockedTypes.add('Necklaces');
    if (worn.has('earrings')) blockedTypes.add('Earrings');
    if (worn.has('watch')) blockedTypes.add('Bracelets');
  }

  return {
    blockedTypes,
    maxPrice: BUDGET_CEILING[dims.budget] != null ? BUDGET_CEILING[dims.budget] : Infinity,
    sizes: dims.sizes,
  };
}

function pickPasses(pick, rules) {
  if (rules.blockedTypes.has(pick.type)) return false;

  /* pick.price is in the shop's minor units (cents), as Liquid reports it. */
  if (rules.maxPrice !== Infinity && (pick.price || 0) / 100 > rules.maxPrice) return false;

  if (needsSizing(pick)) {
    const dimension = SIZED_TYPES[pick.type];
    if (dimension === 'ring' && rules.sizes !== 'ring' && rules.sizes !== 'both') return false;
    if (dimension === 'wrist' && rules.sizes !== 'wrist' && rules.sizes !== 'both') return false;
  }

  return true;
}

/* Advisor notes are typed by the team in the Pick Builder and stored as
   plain text on the metaobject, then interpolated into an innerHTML
   string here, so they have to be escaped. */
function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[char]));
}

/* The canonical NH product card for a handle, as markup.

   sections/gift-guide.liquid renders snippets/nh-card.liquid into an inert
   <template> per unique product. Reading .innerHTML gives the same markup
   the collection PLP produces, already money-formatted for the visitor's
   market, without this file having to know anything about card structure.
   Returns null when the handle has no template. */
function cardMarkup(handle) {
  if (!handle) return null;
  const host = document.getElementById('gift-guide-cards');
  if (!host) return null;
  const tpl = host.querySelector(`template[data-gg-card="${CSS.escape(handle)}"]`);
  return tpl ? tpl.innerHTML : null;
}

/* Deterministic per page load. Computed once at module scope so that a
   re-render (which happens on every consent toggle) shows the same three
   cards in the same order rather than reshuffling under the reader. */
const ROTATION_SEED = Math.floor(Date.now() / 1000);

function rotate(list, count) {
  if (list.length <= count) return list.slice();
  const start = ROTATION_SEED % list.length;
  const out = [];
  for (let i = 0; i < count; i += 1) out.push(list[(start + i) % list.length]);
  return out;
}

/* The recommendations email closes by naming the people who chose the pieces
   ("James and Emily, who recommended your pieces..."). Klaviyo renders with
   Django, which cannot dedupe a list or join it with a conjunction, so the
   finished string is composed here and sent ready to print
   (Camille, 11/08/2026).

   Deduped because both picks in a scenario can come from the same advisor,
   and "Emily and Emily" would be worse than no attribution at all. Order is
   the order the pieces appear in the email. */
function advisorList(picks, isFrench) {
  const names = [];
  picks.forEach((pick) => {
    const name = String(pick.advisor || '').trim();
    if (name && names.indexOf(name) === -1) names.push(name);
  });
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  const conjunction = isFrench ? ' et ' : ' and ';
  return names.slice(0, -1).join(', ') + conjunction + names[names.length - 1];
}

/* The budget the visitor chose, and the prices of what we showed them, for
   the Klaviyo events (Camille, 05/10/2026).

   The monthly review is asked to catch "under $3,000 shown a $4,000 piece",
   and until now it could not: no event carried the q12 answer, so the check
   could only compare picks against the scenario's band, never against what a
   real customer asked for.

   Budget is the raw q12 value (entry | mid | high | premium), the same keys
   as BUDGET_CEILING, so the review can join on it directly. PriceAmounts are
   plain numbers in major units, because the formatted `Price` string
   ("€4.820,00") cannot be compared without parsing locale formats. They are
   in the visitor's presentment currency, which is why Currency travels with
   them: a EUR amount must not be read against a USD ceiling. */
function presentmentCurrency() {
  try {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  } catch (err) {
    return '';
  }
}

function budgetProperties(state, picks) {
  return {
    Budget: answersToDims(state).budget || '',
    Currency: presentmentCurrency(),
    PriceAmounts: (picks || []).map((pick) => (pick.price || 0) / 100),
  };
}

/* Returns the picks to show, plus which scenario they came from and
   whether that was an exact match, so the caller can decide how much
   attribution to claim. */
function chooseRecommendations(state) {
  const dims = answersToDims(state);
  const ranked = SCENARIOS
    .map((scenario) => ({ scenario, score: scoreScenario(dims, scenario) }))
    .filter((entry) => entry.score > -Infinity)
    .sort((a, b) => b.score - a.score);

  if (!ranked.length) return { code: null, picks: [], exact: false };

  const best = ranked[0].scenario.code;
  const exactPicks = PICKS_BY_SCENARIO[best] || [];
  if (exactPicks.length) {
    return { code: best, picks: rotate(exactPicks, 3), exact: true };
  }

  /* No advisor has covered the closest scenario yet, so borrow from the
     next best that is covered AND whose picks are still safe to show this
     particular visitor. */
  const rules = safetyRulesFor(dims);
  for (let i = 1; i < ranked.length; i += 1) {
    const candidate = ranked[i].scenario.code;
    const available = (PICKS_BY_SCENARIO[candidate] || []).filter((pick) => pickPasses(pick, rules));
    if (available.length) {
      return { code: candidate, picks: rotate(available, 3), exact: false };
    }
  }

  return { code: best, picks: [], exact: false };
}

/* ============================================================
   ENGLISH FALLBACKS (flat keys matching schema locales)
   ============================================================ */
const EN = {
  // Welcome
  welcome_badge: 'Gift Guide',
  welcome_heading: 'Start your personal consultation.',
  welcome_body: 'Choosing the right piece of jewelry is deeply personal. Our advisors have chosen pieces for the clients they meet, so your recommendations are considered rather than generic.',
  welcome_cta: 'Begin',
  welcome_time: 'No email required. Takes 1 to 3 minutes.',
  welcome_team_heading: 'Meet your advisors',
  welcome_team_result: 'Your result: 1 to 3 recommendations, curated for you.',
  // Advisor role labels. Uppercasing is CSS, so these stay in sentence case.
  advisor_role_senior_sales: 'Senior Sales',
  advisor_role_vip_specialist: 'VIP Specialist',
  advisor_role_founder: 'Founder and CEO',
  // Q1
  q1_heading: 'Welcome to your personal consultation.',
  q1_question: 'What should we call you?',
  q1_placeholder: 'Your first name',
  q1_skip: 'Skip',
  q1_continue: 'Continue',
  // Q2
  // Q3
  q3_heading_full: 'Take your time.',
  q3_question: 'Who is this for?',
  q3_self_label: 'For myself',
  q3_self_desc: 'You deserve something beautiful.',
  q3_her_label: 'For her',
  q3_her_desc: 'A woman in my life.',
  q3_him_label: 'For him',
  q3_him_desc: 'A man in my life.',
  // Q4
  q4_heading: 'So we can make this personal.',
  q4_question: 'What\'s their first name?',
  q4_placeholder: 'Their name',
  q4_skip: 'Skip',
  q4_continue: 'Continue',
  // Q5
  q5_heading: 'Every piece marks a moment.',
  q5_question_self: 'What brings you here?',
  q5_question_gift: 'What\'s the occasion?',
  q5_self_celebrating_label: 'Celebrating myself',
  q5_self_celebrating_desc: 'A milestone, a promotion, or simply because.',
  q5_self_birthday_label: 'Birthday or holiday',
  q5_self_birthday_desc: 'We will prioritize pieces we keep in stock.',
  q5_self_collection_label: 'Building a collection',
  q5_self_collection_desc: 'Adding to what you have started.',
  q5_gift_milestone_label: 'A milestone',
  q5_gift_milestone_desc: 'Anniversary, promotion, new chapter.',
  q5_gift_birthday_label: 'Birthday or holiday',
  q5_gift_birthday_desc: 'We will prioritize pieces we keep in stock.',
  q5_gift_collection_label: 'Building a collection',
  q5_gift_collection_desc: 'The next chapter in their jewelry story.',
  // Q6
  q6_heading: 'We can build on what {they} already {have}.',
  q6_question: '{DoThey} already own any Nouvel Heritage?',
  q6_mood_label: 'Yes, from the Mood Collection',
  q6_mood_desc: 'Our signature collection.',
  q6_tryme_label: 'Yes, from the Try Me Collection',
  q6_tryme_desc: 'Chains and statement links.',
  q6_latch_label: 'Yes, from the Latch Collection',
  q6_latch_desc: 'Modular cord-lock pieces.',
  q6_other_label: 'No, but {they} {collect} from other brands',
  q6_other_desc: 'A piece to live alongside the others.',
  q6_first_label: 'It would be {their} first fine jewelry piece',
  q6_first_desc: 'The beginning of something.',
  // Q7
  q7_heading: 'Great taste.',
  q7_question: 'What feels right?',
  q7_complete_label: 'Complete the set',
  q7_complete_desc: 'A piece from the same collection, designed to pair with what {they} already {have}.',
  q7_stack_label: 'Keep stacking',
  q7_stack_desc: 'A complementary piece, possibly from a different collection.',
  q7_different_label: 'Something completely different',
  q7_different_desc: 'A new collection, a new chapter.',
  // Q8
  q8_heading: 'Tell us about {their} jewelry.',
  q8_question: 'What {doThey} wear every day?',
  q8_necklace_label: 'A necklace {they} never {take} off',
  q8_necklace_desc: 'We would recommend a bracelet, ring, or earrings instead.',
  q8_bracelet_label: '{TheyRe} building a bracelet or bangle stack',
  q8_bracelet_desc: 'We can add to what is already there.',
  q8_earrings_label: 'A pair of earrings {they} {wear} every single day',
  q8_earrings_desc: 'We would look at necklaces, bracelets, or rings.',
  q8_watch_label: 'A watch is the main piece',
  q8_watch_desc: 'The wrist is taken. We would focus on necklaces and earrings.',
  q8_none_label: '{TheyDont} really have a go-to yet',
  q8_none_desc: 'Open canvas. We can go anywhere.',
  // Q8b
  q8b_heading: 'What\'s in {their} current stack?',
  q8b_question: 'Select all that apply.',
  q8b_monday: 'Mood Monday Morning',
  q8b_business: 'Mood Business Meeting',
  q8b_dinner: 'Mood Dinner Date',
  q8b_brunch: 'Mood Brunch in NY',
  q8b_other_mood: 'Other Mood bangles',
  q8b_other_brands: 'Bangles or bracelets from other brands',
  q8b_continue: 'Continue',
  // Q9
  q9_heading: 'What metals {doThey} wear most?',
  q9_question: 'Select all that apply.',
  q9_yellow: 'Yellow gold',
  q9_white: 'White gold',
  q9_rose: 'Rose gold',
  q9_titanium: 'Titanium',
  q9_continue: 'Continue',
  // Q10
  q10_heading: 'Diamonds.',
  q10_question: '{Their} preference?',
  q10_bold_label: 'Go bold',
  q10_bold_desc: 'Statement diamonds, visible from across the room.',
  q10_subtle_label: 'Subtle accents',
  q10_subtle_desc: 'A whisper of brilliance along the setting.',
  q10_none_label: 'As few as possible',
  q10_none_desc: 'Let the gold and the design do the work.',
  // Q11
  q11_heading: 'How {doThey} wear jewelry?',
  q11_question: 'Which feels closest?',
  q11_layers_label: '{They} {layerAndStack}',
  q11_layers_desc: 'More is more, always curated.',
  q11_statement_label: 'One statement piece at a time',
  q11_statement_desc: 'Fewer pieces, but larger in scale.',
  q11_everyday_label: 'It needs to fit a busy life',
  q11_everyday_desc: 'Comfortable, low-profile, never in the way.',
  q11_minimal_label: 'Minimal and refined',
  q11_minimal_desc: 'Smaller, dainty pieces.',
  // Q11b
  q11b_heading: 'Sizing.',
  q11b_question_self: 'Select all that apply.',
  q11b_question_gift: 'Select all that apply.',
  q11b_ring_label_self: 'I know my ring size',
  q11b_wrist_label_self: 'I know my wrist size',
  q11b_unsure_label_self: 'I do not know either',
  q11b_unsure_desc_self: 'We offer size guides and complimentary exchanges if you are unsure.',
  q11b_ring_label_gift: 'I know {their} ring size',
  q11b_wrist_label_gift: 'I know {their} wrist size',
  q11b_unsure_label_gift: 'I do not know either',
  q11b_unsure_desc_gift: 'We will focus on pieces that are one size or that do not require sizing.',
  q11b_continue: 'Continue',
  // Q5h-collection (him: does he own NH?)
  q5hc_heading: 'We can build on what he already has.',
  q5hc_question: 'Does he already own any Nouvel Heritage?',
  q5hc_mood_label: 'Yes, from the Mood Collection',
  q5hc_mood_desc: 'Our signature collection.',
  q5hc_tryme_label: 'Yes, from the Try Me Collection',
  q5hc_tryme_desc: 'Chains and statement links.',
  q5hc_latch_label: 'Yes, from the Latch Collection',
  q5hc_latch_desc: 'Modular cord-lock pieces.',
  q5hc_other_label: 'No, but he collects from other brands',
  q5hc_other_desc: 'A piece to live alongside the others.',
  q5hc_first_label: 'It would be his first fine jewelry piece',
  q5hc_first_desc: 'The beginning of something.',
  // Q6-him (experience level)
  q6h_heading: 'We can start with what he knows.',
  q6h_question: 'How does he wear jewelry today?',
  q6h_daily_label: 'He wears jewelry every day',
  q6h_daily_desc: 'Rings, a bracelet, a chain. It\'s part of who he is.',
  q6h_minimal_label: 'He keeps it minimal',
  q6h_minimal_desc: 'A watch, maybe a wedding band. That\'s about it.',
  q6h_first_label: 'It would be his first fine jewelry piece',
  q6h_first_desc: 'Something to start with.',
  // Q7-him (what he wears, multi-select)
  q7h_heading: 'What does he already wear?',
  q7h_question: 'Select all that apply.',
  q7h_rings: 'Rings',
  q7h_bracelets: 'Bracelets or bangles',
  q7h_necklaces: 'Necklaces or chains',
  q7h_watch: 'A watch (and nothing else)',
  q7h_nothing: 'Nothing yet',
  q7h_continue: 'Continue',
  // Q8-him (style)
  q8h_heading: 'His style.',
  q8h_question: 'What does he gravitate toward?',
  q8h_bold_label: 'Bold and architectural',
  q8h_bold_desc: 'Pieces that stand out. Geometric. Substantial.',
  q8h_understated_label: 'Minimal and refined',
  q8h_understated_desc: 'Clean lines, close to the skin. Nothing unnecessary.',
  q8h_classic_label: 'Classic with a twist',
  q8h_classic_desc: 'Traditional shapes, unexpected details.',
  // Q10-him (diamonds reworded)
  q10h_heading: 'Diamonds.',
  q10h_question: 'His taste.',
  q10h_bold_label: 'He would wear diamonds',
  q10h_bold_desc: 'Integrated into the design. A considered choice.',
  q10h_subtle_label: 'Subtle if anything',
  q10h_subtle_desc: 'Barely there.',
  q10h_none_label: 'No diamonds',
  q10h_none_desc: 'He would not wear white diamonds.',
  // Q12
  q12_heading: 'Last one.',
  q12_question: 'Where would you like to begin?',
  q12_under3k_label: 'Under $3,000',
  q12_under3k_desc: 'A beautiful first piece.',
  q12_3k6k_label: '$3,000 to $6,000',
  q12_3k6k_desc: 'Something meaningful.',
  q12_6k10k_label: '$6,000 to $10,000',
  q12_6k10k_desc: 'A significant gift.',
  q12_open_label: 'I\'m open',
  q12_open_desc: 'Show me the best.',
  // Results
  results_full_title: 'Your Personal Edit',
  results_view_piece: 'Discover this piece',
  results_recommends: '{name} recommends:',
  results_empty: 'We would rather talk this one through than guess. Speak to an advisor and we will find the right piece with you.',
  save_heading: 'Receive your recommendations',
  save_subtext: 'We will email you a summary of your personalized picks.',
  save_email_placeholder: 'Your email address',
  save_email_label: 'Email address',
  save_email_invalid: 'Please enter a valid email address.',
  save_email_consent_required: 'Please agree to receive your recommendations by email to continue.',
  save_submit: 'Email me my recommendations',
  save_sending: 'Sending…',
  save_error: 'Something went wrong and your details were not saved. Please try again.',
  save_consent: 'Email me my personal edit and Nouvel Heritage news, including 10% off my first order. We store your answers to make the recommendations. You can unsubscribe at any time.',
  save_success_heading: 'Done. Check your inbox.',
  save_success_text: 'Your personal edit is on its way, along with a little welcome gift.',
  cta_explore: 'Explore All Collections',
  cta_start_over: 'Start Over',
  // UI
  back: 'Back',
  /* Kept, and still doing real work: it is the progress bar's aria-valuetext
     and the live-region announcement. The bar shows no number. */
  step_counter: '{current} of {total}',
  /* Accessible name for the progress bar. role="progressbar" is a widget
     role, so WCAG 4.1.2 wants a name, and "3 of 10" on its own does not say
     three of ten WHAT. */
  progress_label: 'Consultation progress',
  question_region: 'Question {number}',
  results_note_self_selfcare: 'this is yours. You have earned it.',
  results_note_self: 'you know what you want. Here is what we would recommend.',
  results_note_named: 'here is what we would choose for {recipient}.',
  results_note_default: 'here is what we would recommend.',
  results_note_with_name: '{name}, {note}',
  noscript: 'Please enable JavaScript to use the Gift Guide consultation.',
};

/* ============================================================
   NAME HELPER: capitalize first letter ("bob" -> "Bob")
   ============================================================ */
function capitalizeName(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/* ============================================================
   PRONOUN HELPER
   ============================================================ */
/* ============================================================
   PRONOUNS

   Returns BOTH an English and a French token set. Each locale's strings only
   reference their own token names, so there is no collision, and the shared
   keys (they, They, them, their...) resolve to whichever language is on the
   page.

   WHY FRENCH NEEDED ITS OWN SET (fixed 2026-08-11)
   This function used to return English words only, while 15 French strings
   used the tokens. A French visitor read "Comment do they porte ses bijoux ?"
   and "Ce serait their première pièce de joaillerie". About a third of the
   quiz was English words spliced into French sentences.

   French is harder than English here for two reasons:

   1. VERB AGREEMENT. "vous possédez" but "elle possède". English gets away
      with one form. The saving grace is that "Marie possède", "elle possède"
      and "il possède" are all third person singular, so French needs exactly
      ONE split -- vous versus everyone else -- not one per case.

   2. ELISION. "ce que vous" but "ce qu'elle". A bare {they} token cannot
      carry that, so tokens like queThey swallow the preceding word.

   Where inversion is involved ("portez-vous" / "porte-t-elle"), the whole
   phrase is one token rather than trying to assemble it from parts.
   ============================================================ */
function pronouns(state, locale) {
  const who = state.whoFor || '';
  const isSelf = who === 'self';
  const name = state.recipientName || '';
  const named = !!name;
  const isFrench = String(locale || '').toLowerCase().startsWith('fr');
  const fem = who !== 'him';

  if (isFrench) {
    /* Subject: vous / the name / elle / il. */
    const sujet = isSelf ? 'vous' : (name || (fem ? 'elle' : 'il'));
    const Sujet = isSelf ? 'Vous' : (name || (fem ? 'Elle' : 'Il'));
    /* Inversion for questions. A named recipient keeps the name AND the
       inverted pronoun, which is how French actually asks it:
       "Marie porte-t-elle...". */
    const inv = fem ? '-t-elle' : '-t-il';
    const nameInv = named ? `${name} ` : '';

    return {
      they: sujet,
      They: Sujet,
      them: sujet,
      /* Possessives agree with the noun that follows, not the owner, so the
         three genders are separate tokens and each string picks the right
         one. */
      son: isSelf ? 'votre' : 'son',
      sa: isSelf ? 'votre' : 'sa',
      ses: isSelf ? 'vos' : 'ses',
      Sa: isSelf ? 'Votre' : 'Sa',
      /* "ce que vous" vs "ce qu'elle" -- the token carries the elision. */
      queThey: isSelf ? 'que vous' : (named ? `que ${name}` : (fem ? "qu'elle" : "qu'il")),
      /* Whole inverted phrases. */
      /* Capitalised only when the verb actually starts the sentence. With a
         name it does not: "Marie a-t-elle déjà..." is correct, whereas
         "a-t-elle déjà..." alone opened a question in lower case. */
      possedeDeja: isSelf
        ? 'Avez-vous déjà'
        : (named ? `${name} a${inv} déjà` : `A${inv} déjà`),
      porteQuoi: isSelf ? 'Que portez-vous' : `Que porte${named ? ' ' + name : inv}`,
      porteLePlus: isSelf ? 'portez-vous le plus' : `${nameInv}porte${inv} le plus`,
      porteLesBijoux: isSelf ? 'portez-vous vos bijoux' : `${nameInv}porte${inv} ses bijoux`,
      /* Verbs: second person plural versus third person singular. */
      collectionne: isSelf ? 'collectionnez' : 'collectionne',
      quitte: isSelf ? 'quittez' : 'quitte',
      constitue: isSelf ? 'constituez' : 'constitue',
      naPas: isSelf ? "n'avez pas" : "n'a pas",
      superpose: isSelf ? 'superposez et accumulez' : 'superpose et accumule',
      /* Q11b sizing, gift path only (the self strings say "ma taille" and
         need no token). French cannot bolt a name onto the possessive the way
         English does with "Sophie's", so the whole noun phrase is the token
         and the sentence restructures around the name:
           no name -> "sa taille de bague"
           named   -> "la taille de bague de Sophie"                       */
      tailleBague: named ? `la taille de bague de ${name}` : 'sa taille de bague',
      taillePoignet: named ? `la taille de poignet de ${name}` : 'sa taille de poignet',
    };
  }

  /* ENGLISH VERB AGREEMENT (fixed 2026-08-18)

     English needs exactly one split too, but it is not the same split as
     French. The subject is one of three things:

       self      "you"      -> bare form   ("you layer and stack")
       named     "Sophie"   -> third person singular, -s
       unnamed   "they"     -> bare form, because singular they takes the
                              plural verb ("they layer and stack")

     So every verb that appears in a string alongside {they} / {They} has to
     be a TOKEN, never typed into the copy. `q11_layers_label` read
     "{They} layer and stack" with the verbs hardcoded, which rendered
     "Sophie layer and stack" on the live preview. That is the same class of
     fault as the French pronoun bug fixed on 11/08/2026, in the other
     language: a token was doing the pronoun and the copy was doing the verb,
     and only one of the two agreed.

     `layerAndStack` mirrors the French `superpose` token, which carries
     "superpose et accumule" as one unit for the same reason: splitting a
     coordinated verb pair into two tokens gains nothing and doubles the
     number of places the agreement can be got wrong. */
  const s = named ? 's' : '';

  return {
    isSelf,
    named,
    they: isSelf ? 'you' : (name || 'they'),
    They: isSelf ? 'You' : (name || 'They'),
    them: isSelf ? 'you' : (name || 'them'),
    their: isSelf ? 'your' : (named ? name + '\'s' : 'their'),
    Their: isSelf ? 'Your' : (named ? name + '\'s' : 'Their'),
    theyRe: isSelf ? 'you\'re' : (named ? name + ' is' : 'they\'re'),
    TheyRe: isSelf ? 'You\'re' : (named ? name + ' is' : 'They\'re'),
    doThey: isSelf ? 'do you' : (named ? 'does ' + name : 'do they'),
    DoThey: isSelf ? 'Do you' : (named ? 'Does ' + name : 'Do they'),
    theyHave: isSelf ? 'you have' : (named ? name + ' has' : 'they have'),
    /* Verbs. `isSelf` and the unnamed third person both take the bare form,
       so the only case that inflects is the named one. */
    have: named && !isSelf ? 'has' : 'have',
    take: isSelf ? 'take' : 'take' + s,
    wear: isSelf ? 'wear' : 'wear' + s,
    collect: isSelf ? 'collect' : 'collect' + s,
    layerAndStack: isSelf
      ? 'layer and stack'
      : (named ? 'layers and stacks' : 'layer and stack'),
    theyDont: isSelf ? 'you don\'t' : (named ? name + ' doesn\'t' : 'they don\'t'),
    TheyDont: isSelf ? 'You don\'t' : (named ? name + ' doesn\'t' : 'They don\'t'),
  };
}

/* ============================================================
   QUIZ ENGINE
   ============================================================ */
class GiftGuideQuiz extends HTMLElement {
  constructor() {
    super();
    this.sessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    this.history = [];
    this.state = {
      screen: 'welcome',
      currentQ: null,
      answers: {},
      whoFor: null,
      userName: '',
      recipientName: '',
      qNumber: 0,
    };
    this.translations = {};
  }

  connectedCallback() {
    this.initTranslations();
    this.render();
  }

  /* ---------- translations ---------- */
  initTranslations() {
    try {
      const el = document.getElementById('gift-guide-translations');
      if (el) this.translations = JSON.parse(el.textContent);
    } catch (e) { /* use fallbacks */ }
  }

  t(key, vars = {}) {
    let text = this.translations[key] || EN[key] || key;
    Object.entries(vars).forEach(([k, v]) => {
      text = text.replace(new RegExp('\\{' + k + '\\}', 'g'), v);
    });
    /* Last, so it also covers a symbol arriving through an interpolated
       variable rather than only ones baked into the locale string. */
    return localizeCurrency(text);
  }

  /* ---------- pronoun-aware translate ---------- */
  tp(key, extraVars = {}) {
    /* The page language drives which pronoun set is used. Read from the
       document rather than stored on state, because Shopify sets it per
       locale and it cannot change mid-quiz. */
    const p = pronouns(this.state, document.documentElement.lang);
    const vars = { ...p, ...extraVars };
    if (this.state.userName) vars.name = this.state.userName;
    return this.t(key, vars);
  }

  /* ---------- estimated total ---------- */
  estTotal() {
    return this.state.whoFor === 'him' ? 8 : 10;
  }

  /* ---------- navigation ---------- */
  getNext(qId, answer) {
    const s = this.state;
    const isHim = s.whoFor === 'him';

    switch (qId) {
      case 'q1': return 'q3';
      case 'q3':
        if (s.whoFor === 'self') return 'q5';
        return 'q4';
      case 'q4': return 'q5';
      case 'q5':
        if (isHim) return answer === 'collection' ? 'q5hc' : 'q6h';
        if (answer === 'collection') return 'q6';
        return 'q8';
      case 'q6':
        if (['mood', 'tryme', 'latch'].includes(answer)) return 'q7';
        return 'q8';
      case 'q7': return 'q9';
      case 'q8':
        return answer === 'bracelet_stack' ? 'q8b' : 'q9';
      case 'q8b': return 'q9';
      /* --- Him path --- */
      case 'q5hc': return 'q6h';
      case 'q6h':
        return answer === 'first' ? 'q9' : 'q7h';
      case 'q7h':
        return 'q8h';
      case 'q8h':
        return 'q9';
      /* --- Shared from here --- */
      case 'q9': return isHim ? 'q10h' : 'q10';
      case 'q10': return 'q11';
      case 'q10h': return 'q11b';
      case 'q11': return 'q11b';
      case 'q11b': return 'q12';
      case 'q12': return 'results';
      default: return null;
    }
  }

  answer(qId, value) {
    this.state.answers[qId] = value;

    // Update state shortcuts
    if (qId === 'q1') this.state.userName = capitalizeName(value) || '';
    if (qId === 'q3') this.state.whoFor = value;
    if (qId === 'q4') this.state.recipientName = capitalizeName(value) || '';

    const next = this.getNext(qId, value);
    if (!next) return;

    this.history.push(qId);
    this.state.qNumber++;

    if (next === 'results') {
      this.state.screen = 'results';
    } else {
      this.state.currentQ = next;
    }

    this.animateOut(() => this.render());
    this.logEvent('answer', { question: qId, value });
  }

  goBack() {
    if (!this.history.length) return;
    const prev = this.history.pop();
    delete this.state.answers[this.state.currentQ];
    this.state.currentQ = prev;
    this.state.qNumber--;
    this.animateOut(() => this.render());
  }

  restart() {
    this.history = [];
    /* A second run through is a second completion, so clear the once-per-
       session guard or the retake would be invisible. */
    try {
      sessionStorage.removeItem(GG_RESULTS_FIRED_KEY);
    } catch (err) {
      /* nothing to clear */
    }
    /* Otherwise the bar would animate DOWN from wherever it was to zero on
       the first question of the new run. */
    this._progressPercent = 0;
    this.state = {
      screen: 'welcome', currentQ: null, answers: {},
      whoFor: null, userName: '', recipientName: '', qNumber: 0,
    };
    this.animateOut(() => this.render());
  }

  start() {
    this.state.screen = 'quiz';
    this.state.currentQ = 'q1';
    this.logEvent('started');
    this.animateOut(() => this.render());
  }

  /* ---------- animation ---------- */
  animateOut(cb) {
    const inner = this.querySelector('.gg-inner');
    if (inner) {
      inner.classList.add('gg-inner--exit');
      setTimeout(cb, 250);
    } else {
      cb();
    }
  }

  /* ---------- event delegation ---------- */
  handleClick(e) {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;
    // WCAG 4.1.2 (#48) — defense-in-depth: never fire actions for a button
    // that reports itself as disabled, even if the event somehow reaches us
    // (keyboard activation on browsers that ignore aria-disabled, programmatic
    // dispatch, etc.).
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

    const action = btn.dataset.action;
    const q = btn.dataset.q;
    const val = btn.dataset.val;

    switch (action) {
      case 'start': this.start(); break;
      case 'answer': this.answer(q, val); break;
      case 'answer-text': {
        const input = this.querySelector('.gg-text-input');
        this.answer(q, input ? input.value.trim() : '');
        break;
      }
      case 'skip': this.answer(q, ''); break;
      case 'back': this.goBack(); break;
      case 'multi-toggle': {
        const cb = btn.querySelector('.gg-checkbox');
        btn.classList.toggle('gg-option--selected');
        const isSelected = btn.classList.contains('gg-option--selected');
        // role="checkbox" + aria-checked live on the button itself; the
        // inner .gg-checkbox span is purely decorative (aria-hidden).
        btn.setAttribute('aria-checked', String(isSelected));

        // q11b: mutual exclusion between "I don't know either" and ring/wrist options
        if (q === 'q11b' && isSelected) {
          const isUnknown = typeof val === 'string' && val.indexOf('unknown_') === 0;
          const siblings = this.querySelectorAll('.gg-option--multi[data-q="q11b"]');
          siblings.forEach((sib) => {
            if (sib === btn) return;
            const sibVal = sib.dataset.val || '';
            const sibIsUnknown = sibVal.indexOf('unknown_') === 0;
            const shouldClear = isUnknown ? !sibIsUnknown : sibIsUnknown;
            if (shouldClear && sib.classList.contains('gg-option--selected')) {
              sib.classList.remove('gg-option--selected');
              sib.setAttribute('aria-checked', 'false');
            }
          });
        }
        break;
      }
      case 'multi-submit': {
        const selected = [...this.querySelectorAll('.gg-option--selected')].map(el => el.dataset.val);
        if (selected.length) this.answer(q, selected);
        break;
      }
      case 'save-email': this.handleSaveEmail(); break;
      case 'consent-toggle': {
        // role="checkbox" + aria-checked live on the button (btn); the inner
        // .gg-checkbox span is decorative (aria-hidden).
        const field = btn.dataset.field;
        const checked = btn.getAttribute('aria-checked') !== 'true';
        btn.setAttribute('aria-checked', String(checked));
        btn.classList.toggle('gg-consent--checked', checked);
        if (field === 'consent') this._consentChecked = checked;
        break;
      }
      case 'restart': this.restart(); break;
    }
  }

  handleKeydown(e) {
    if (e.key === 'Enter') {
      const input = e.target.closest('.gg-text-input');
      if (input) {
        const q = input.dataset.q;
        if (input.value.trim()) this.answer(q, input.value.trim());
      }
    }
  }

  // WCAG 3.3.1 — clear the email error state as soon as the user starts to
  // correct the field, so the stale error message and aria-invalid don't
  // linger past the user's recovery attempt.
  handleInput(e) {
    if (e.target && e.target.classList && e.target.classList.contains('gg-email-input')) {
      e.target.removeAttribute('aria-invalid');
      e.target.classList.remove('gg-email-input--error');
      const errorEl = this.querySelector('#gg-email-error');
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.setAttribute('hidden', '');
      }
    }
  }

  handleSaveEmail() {
    const input = this.querySelector('.gg-email-input');
    const errorEl = this.querySelector('#gg-email-error');
    if (!input) return;

    // WCAG 3.3.1 Error Identification — instead of silently bailing on bad
    // input, surface the failure both visually (aria-invalid + visible
    // message) and to AT (role=alert + aria-live="assertive").
    const showError = (msgKey) => {
      input.setAttribute('aria-invalid', 'true');
      input.classList.add('gg-email-input--error');
      if (errorEl) {
        const message = this.t(msgKey);
        errorEl.removeAttribute('hidden');
        errorEl.textContent = '';
        requestAnimationFrame(() => {
          if (!errorEl.hasAttribute('hidden')) errorEl.textContent = message;
        });
      }
      input.focus();
    };

    // Basic email validity check — same logic as before but now surfaces
    // an error instead of silently failing.
    const value = (input.value || '').trim();
    const isValidEmail = /.+@.+\..+/.test(value);
    if (!isValidEmail) {
      showError('save_email_invalid');
      return;
    }
    if (!this._consentChecked) {
      showError('save_email_consent_required');
      return;
    }

    // Clear any prior error state once submission proceeds.
    input.removeAttribute('aria-invalid');
    input.classList.remove('gg-email-input--error');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.setAttribute('hidden', '');
    }

    /* The old code showed the success panel here, unconditionally, before
       anything had been sent anywhere. That is what let an empty logEvent()
       masquerade as a working signup for four months. Success is now only
       claimed once Klaviyo has accepted the profile. */
    const submit = this.querySelector('[data-action="save-email"]');
    const submitHadFocus = !!submit && document.activeElement === submit;
    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-disabled', 'true');
      submit.textContent = this.t('save_sending');
    }

    this.logEvent('email_captured', {
      email: value,
      /* Always true now. The single required checkbox states plainly that
         submitting subscribes them, so consent is given at submit or the
         form does not submit at all (Camille, 11/08/2026). */
      marketing: true,
      answers: this.state.answers,
    }).then(() => {
      const saveBox = this.querySelector('.gg-save');
      if (saveBox) {
        saveBox.innerHTML = `
          <p class="gg-save__heading" tabindex="-1">${this.t('save_success_heading')}</p>
          <p class="gg-save__text">${this.t('save_success_text')}</p>
        `;
        const active = document.activeElement;
        if (submitHadFocus && (!active || active === document.body)) {
          saveBox.querySelector('.gg-save__heading')?.focus();
        }
      }
    }).catch((err) => {
      /* Surface the failure to the customer AND to the console. Telling
         someone their details are saved when they are not is worse than
         telling them to try again: they will not come back to re-enter it,
         and we lose the address permanently. */
      console.error('[gift-guide] Klaviyo submission failed', err);
      if (submit) {
        submit.disabled = false;
        submit.removeAttribute('aria-disabled');
        submit.textContent = this.t('save_submit');
      }
      showError('save_error');
    });
  }

  /* ---------- Klaviyo ----------

     This function had an EMPTY BODY. It validated the address, showed the
     customer "check your inbox", and threw the address away. Every email
     entered on this page since it went live in April was discarded while the
     consent copy promised we were storing it.

     Two things are deliberate here.

     1. We do NOT use Klaviyo's onsite `_learnq` object.
        snippets/nh-consent-default-deny.liquid treats Klaviyo as a marketing
        pixel and blocks it before consent is given. Since deny is the default,
        `_learnq` would fail silently for most visitors -- reproducing the exact
        bug we are fixing, just one layer down. We POST to Klaviyo's client
        endpoints instead, which take only the PUBLIC key. No secret in the
        theme.

     2. The consent split matches what the page actually asks for.
        The required checkbox consents to storing the answers and the address,
        so we always write the profile. The optional checkbox is the marketing
        one, so ONLY that subscribes to Newsletter. Ticking neither is not
        possible; ticking only the first must not sign anyone up.
     ---------------------------------------------------------------- */
  /* ---------- funnel measurement ----------

     The original rule here was "only the email submission leaves the browser",
     because answer-by-answer telemetry on a default-deny page would be
     tracking without a lawful basis. That reasoning still stands and the
     per-answer events are still discarded. What changed on 15/09/2026 is that
     two aggregate events now fire, and only for visitors who have actively
     accepted analytics:

       started        someone began the quiz
       results_shown  someone reached their recommendations

     Neither carries an answer, a name or a piece. They carry the scenario
     code, the locale and which placement sent the visitor. That is enough to
     report a completion rate and compare placements, and not enough to profile
     anybody.

     CONSENT IS CHECKED AT SEND TIME, NOT AT PAGE LOAD. The banner resolves
     after this element boots, so reading it once on connect would deny
     everyone. `email_captured` is exempt from the analytics check: the visitor
     has just typed their address and ticked the required box asking us to
     store it, which is a different lawful basis to analytics.

     CONSEQUENCE FOR THE MONTHLY REVIEW: these counts are visitors who accepted
     analytics, not all visitors. They will read LOWER than GA4 sessions on the
     page, and the gap is consent rate, not drop-off. Report them as such. */
  analyticsAllowed() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;
    try {
      return privacy.analyticsProcessingAllowed() === true;
    } catch (err) {
      return false;
    }
  }

  /* Shopify's own per-visitor consent id. Stable for the session, already
     lawful to hold, and it saves minting a second identifier of our own.
     Klaviyo merges this anonymous profile into the real one if the visitor
     later submits an email, so a start and its eventual capture join up. */
  anonymousId() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    try {
      if (privacy && typeof privacy.consentId === 'function') {
        const id = privacy.consentId();
        if (id) return String(id);
      }
    } catch (err) {
      /* fall through */
    }
    return null;
  }

  entryPoint() {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('entry_point');
      if (fromUrl) {
        sessionStorage.setItem(GG_ENTRY_POINT_KEY, fromUrl);
        return fromUrl;
      }
      return sessionStorage.getItem(GG_ENTRY_POINT_KEY) || 'direct';
    } catch (err) {
      return 'direct';
    }
  }

  /* House pattern, same as contact-hub.js and high-intent-visit-tracking.js:
     publish to the Customer Events sandbox and mirror to dataLayer. Note that
     on this storefront there is no GTM and no gtag on the page itself, so the
     dataLayer half is parity only; the sandbox is what any pixel subscribes
     to. Klaviyo below is what the monthly review actually reads. */
  publishToShopify(eventName, payload) {
    try {
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish(eventName, payload);
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...payload });
    } catch (err) {
      /* Analytics must never break the quiz. */
    }
  }

  /* An event with no email. Klaviyo needs at least one identifier, so this
     goes out against the anonymous consent id. Without one we publish to the
     sandbox and stop, rather than inventing an identifier. */
  sendAnonymousKlaviyoEvent(metricName, properties) {
    const anonymousId = this.anonymousId();
    if (!anonymousId) return Promise.resolve();
    return this.klaviyoFetch('events', {
      data: {
        type: 'event',
        attributes: {
          properties,
          metric: { data: { type: 'metric', attributes: { name: metricName } } },
          profile: { data: { type: 'profile', attributes: { anonymous_id: anonymousId } } },
        },
      },
    }).catch(() => {
      /* A dropped analytics beacon is not worth surfacing to the visitor. */
    });
  }

  logEvent(type, data = {}) {
    /* The visitor asked for this one and consented to it explicitly. */
    if (type === 'email_captured') return this.sendToKlaviyo(data);

    /* Per-answer telemetry stays discarded, consent or not. */
    if (type !== 'started' && type !== 'results_shown') return Promise.resolve();

    if (!this.analyticsAllowed()) return Promise.resolve();

    const isFrench = (document.documentElement.lang || '').toLowerCase().startsWith('fr');
    const base = { EntryPoint: this.entryPoint(), Locale: isFrench ? 'fr' : 'en' };

    if (type === 'started') {
      this.publishToShopify('gift_guide_started', base);
      return this.sendAnonymousKlaviyoEvent(KLAVIYO_METRIC_STARTED, base);
    }

    /* renderResults() runs again on every re-render of the results screen, so
       without this the completion count would inflate on any repaint. Once per
       tab session is the honest unit. */
    try {
      if (sessionStorage.getItem(GG_RESULTS_FIRED_KEY)) return Promise.resolve();
      sessionStorage.setItem(GG_RESULTS_FIRED_KEY, '1');
    } catch (err) {
      /* Private mode: accept the risk of a double count over losing the event. */
    }

    const match = chooseRecommendations(this.state);
    const properties = {
      ...base,
      Scenario: match.code || '',
      ExactMatch: !!match.exact,
      RecommendationCount: match.picks ? match.picks.length : 0,
      ...budgetProperties(this.state, match.picks),
    };
    this.publishToShopify('gift_guide_results_shown', properties);
    return this.sendAnonymousKlaviyoEvent(KLAVIYO_METRIC_RESULTS, properties);
  }

  klaviyoFetch(path, payload) {
    return fetch(`https://a.klaviyo.com/client/${path}/?company_id=${KLAVIYO_PUBLIC_KEY}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    }).then((res) => {
      /* Klaviyo answers 202 on success and returns a body only on failure. */
      if (!res.ok) {
        return res.text().then((text) => {
          throw new Error(`Klaviyo ${path} responded ${res.status}: ${text.slice(0, 200)}`);
        });
      }
      return true;
    });
  }

  /* Flatten the quiz answers into profile properties. Arrays (the
     multi-selects) are joined so they read sensibly in Klaviyo rather than
     arriving as "[object Object]". */
  quizProperties(answers) {
    const props = {
      gift_guide_completed_at: new Date().toISOString(),
      gift_guide_locale: document.documentElement.lang || 'en',
    };
    Object.keys(answers || {}).forEach((key) => {
      const value = answers[key];
      if (value == null || value === '') return;
      props[`gift_guide_${key}`] = Array.isArray(value) ? value.join(', ') : String(value);
    });
    const match = chooseRecommendations(this.state);
    if (match.code) props.gift_guide_scenario = match.code;
    if (match.picks && match.picks.length) {
      props.gift_guide_recommended = match.picks.map((p) => p.title).join(' | ');
    }
    return props;
  }

  sendToKlaviyo({ email, marketing, answers }) {
    const isFrench = (document.documentElement.lang || '').toLowerCase().startsWith('fr');

    /* Always: store the profile and the answers. This is what the required
       consent checkbox asks permission for. */
    const profile = this.klaviyoFetch('profiles', {
      data: {
        type: 'profile',
        attributes: {
          email,
          properties: this.quizProperties(answers),
        },
      },
    });

    /* The event is what actually triggers the follow-up email. A profile
       write on its own triggers nothing in Klaviyo, which is why storing the
       address was never going to be enough to send anyone anything.

       It fires regardless of the marketing checkbox. Sending someone the
       recommendations they just asked for is what the REQUIRED consent covers
       ("stores your quiz answers and email to provide your personalized
       recommendations"); the optional box is about newsletters, which is a
       different thing. Keeping them separate is the whole point.

       The pieces are frozen into the event rather than looked up at send
       time, so the email shows what the customer was actually shown. */
    const match = chooseRecommendations(this.state);
    const event = this.klaviyoFetch('events', {
      data: {
        type: 'event',
        attributes: {
          properties: {
            Scenario: match.code || '',
            ExactMatch: !!match.exact,
            Locale: isFrench ? 'fr' : 'en',
            RecommendationCount: match.picks.length,
            /* Added 15/09/2026 so the three funnel metrics can be sliced by the
               same dimension. Without it this last step could not be compared
               against the starts and completions above it. */
            EntryPoint: this.entryPoint(),
            /* Added 05/10/2026, see budgetProperties. */
            ...budgetProperties(this.state, match.picks),
            /* Ready-to-print for the email's closing line. See advisorList. */
            Advisors: advisorList(match.picks, isFrench),
            Recommendations: match.picks.map((pick) => ({
              Title: pick.cardTitle || pick.title,
              GoldColor: pick.goldColor || '',
              Price: pick.priceMoney || '',
              ImageURL: pick.imageUrl || '',
              /* Absolute, for the same reason as the image: a relative path
                 in an inbox points nowhere. */
              URL: new URL(pick.url, window.location.origin).href,
              Advisor: pick.advisor || '',
              Why: pick.why || '',
            })),
          },
          metric: { data: { type: 'metric', attributes: { name: KLAVIYO_METRIC } } },
          profile: { data: { type: 'profile', attributes: { email } } },
        },
      },
    });

    if (!marketing) return Promise.all([profile, event]);

    /* Only with the optional marketing box ticked. French visitors also go on
       the language list so they receive French campaigns. */
    const lists = [KLAVIYO_NEWSLETTER_LIST];
    if (isFrench) lists.push(KLAVIYO_FRENCH_LIST);

    return Promise.all([profile, event]).then(() => Promise.all(lists.map((listId) => this.klaviyoFetch('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                /* Stated explicitly rather than left to Klaviyo's default.
                   Omitting this subscribes to MARKETING anyway, but consent
                   should be written down, not inherited from a default that
                   could change under us. */
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    }))));
  }

  /* ---------- personal note for results ---------- */
  personalNote() {
    const name = this.state.userName;
    const p = pronouns(this.state, document.documentElement.lang);
    let note;
    if (p.isSelf) {
      note = this.state.answers.q5 === 'selfcare'
        ? this.t('results_note_self_selfcare')
        : this.t('results_note_self');
    } else if (this.state.recipientName) {
      note = this.t('results_note_named').split('{recipient}').join(escapeHtml(this.state.recipientName));
    } else {
      note = this.t('results_note_default');
    }
    if (!name) return note;
    return this.t('results_note_with_name').split('{name}').join(escapeHtml(name)).split('{note}').join(note);
  }

  /* ============================================================
     RENDER
     ============================================================ */
  render() {
    let html = '';
    if (this.state.screen === 'welcome') html = this.renderWelcome();
    else if (this.state.screen === 'results') html = this.renderResults();
    else html = this.renderQuestion();

    this.innerHTML = html;

    // Re-attach listeners (delegation on the element)
    this.onclick = (e) => this.handleClick(e);
    this.onkeydown = (e) => this.handleKeydown(e);
    this.oninput = (e) => this.handleInput(e);

    // WCAG 4.1.3 Status Messages — announce the new step's heading to screen
    // readers so users of assistive tech know a new question/screen loaded.
    this.announceStep();

    // F-05 (2026-05-12): move keyboard + AT cursor to the new step's heading
    // before any other focus management runs. innerHTML replacement wipes the
    // screen reader virtual cursor, so we re-anchor it at the heading. A
    // tabindex="-1" lets us focus the heading without making it a tab stop.
    const heading = this.querySelector('h1, h2, .gg-question__title, .gg-welcome__title, .gg-results__title');
    if (heading) {
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      // requestAnimationFrame so the focus call lands after layout is settled.
      requestAnimationFrame(() => heading.focus({ preventScroll: true }));
    }

    // Auto-focus text inputs — only on forward navigation (initial render or
    // after answering a previous question), never on a back-step. Otherwise
    // the cursor jumps into the input before the screen reader has finished
    // reading the question heading. F-28 + F-05 (2026-05-12).
    const textInput = this.querySelector('.gg-text-input');
    if (textInput && this.state.history && this.state.history.length > 0) {
      setTimeout(() => textInput.focus(), 350);
    }

    // WCAG 2.3.3 / 2.2.2 — respect prefers-reduced-motion. We have no
    // import system in this file, so check the media query inline.
    const reduceMotion = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Let the progress fill grow into its new width. The element was created
    // fresh by the innerHTML replacement above, so it has no previous
    // computed width for CSS to transition from; progressHtml() renders it at
    // the PREVIOUS percentage and the target is applied on the next frame,
    // which is what gives the transition something to animate. Skipped
    // entirely under reduced motion, where the fill is already at its final
    // width and .gg-progress__fill has transition: none.
    const progressFill = this.querySelector('.gg-progress__fill');
    if (progressFill && !reduceMotion) {
      requestAnimationFrame(() => {
        progressFill.style.width = (progressFill.dataset.ggProgressTo || '0') + '%';
      });
    }

    window.scrollTo({ top: 0, behavior: reduceMotion ? 'instant' : 'smooth' });
  }

  /* ---------- announce step (WCAG 4.1.3) ---------- */
  announceStep() {
    // Ensure a live region exists outside the rendered HTML so innerHTML
    // replacements don't destroy it. Appended as a sibling within this
    // element but not written over because render() replaces innerHTML
    // fully — so we append it after every render.
    let live = document.getElementById('gift-guide-live-region');
    if (!live) {
      live = document.createElement('div');
      live.id = 'gift-guide-live-region';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      live.className = 'visually-hidden';
      // Attach as a sibling so innerHTML on this component doesn't wipe it
      this.parentNode?.insertBefore(live, this.nextSibling);
    }

    // Build the announcement from the rendered heading.
    // WCAG 4.1.2 / 4.1.3 (#32) — innerHTML replacement destroys the DOM tree
    // each render, which clears the screen reader's reading position. The
    // live-region announcement now includes step context ("Question N of M:
    // <heading>") so AT users know both what changed and where they are.
    const heading = this.querySelector('h1, h2, .gg-question__title, .gg-welcome__title, .gg-results__title');
    const headingText = heading?.textContent?.trim();
    if (!headingText) return;

    let message = headingText;
    if (this.state.screen === 'quiz' && this.state.qNumber > 0) {
      const label = this.t('step_counter', {
        current: this.state.qNumber,
        total: this.estTotal(),
      });
      message = `${label}. ${headingText}`;
    }

    // Clear first so the same message re-announces when revisiting a screen
    live.textContent = '';
    setTimeout(() => { live.textContent = message; }, 50);
  }

  /* ---------- welcome ---------- */
  renderWelcome() {
    /* No avatars. They were grey squares holding an initial, which read as an
       unfinished build rather than as restraint (Camille, 2026-08-11). Names,
       roles and one line each stand on their own. Real portraits can be added
       later; until then, saying less is better than showing a placeholder. */
    const teamHtml = TEAM_ORDER.map((key) => {
      const member = TEAM[key];
      return `
      <div class="gg-team-member">
        <div class="gg-team-member__name">${member.name}</div>
        <div class="gg-team-member__role">${this.t(member.roleKey)}</div>
      </div>
    `;
    }).join('');

    /* Split layout when the merchant has set a welcome image, centred when
       they have not. The section renders the image into an inert <template>,
       so this reads its markup rather than building an <img> and losing
       Shopify's responsive srcset. */
    const imageTpl = document.getElementById('gift-guide-welcome-image');
    const imageHtml = imageTpl ? imageTpl.innerHTML : '';
    const side = this.dataset.welcomeImageSide === 'right' ? 'right' : 'left';
    /* Two separate modifier classes, not one interpolated string. Building
       `split split--left` inline produces a stray `split--left` class that
       matches nothing, which is exactly the kind of silent CSS miss that only
       shows up as "the image is on the wrong side" weeks later. */
    const layout = imageHtml ? 'split' : 'centered';
    const sideClass = imageHtml ? ` gg-welcome--split--${side}` : '';

    const invitation = `
      <div class="gg-welcome__invitation">
        <div class="gg-welcome__badge">${this.t('welcome_badge')}</div>
        <h1 class="gg-welcome__heading">${this.t('welcome_heading')}</h1>
        <p class="gg-welcome__body">${this.t('welcome_body')}</p>
        <button class="gg-btn gg-btn--primary" data-action="start">${this.t('welcome_cta')}</button>
        <p class="gg-welcome__time">${this.t('welcome_time')}</p>
      </div>
    `;

    /* The image is decorative: it carries no information the copy does not
       already give, and an empty alt keeps it out of the screen-reader flow
       rather than announcing a filename. */
    const media = imageHtml
      ? `<div class="gg-welcome__media" aria-hidden="true">${imageHtml}</div>`
      : '';

    return `
      <div class="gg-inner gg-welcome gg-welcome--${layout}${sideClass}" role="region" aria-label="${this.t('welcome_badge')}">
        <div class="gg-welcome__lead">
          ${media}
          ${invitation}
        </div>

        <div class="gg-welcome__team" role="region" aria-label="${this.t('welcome_team_heading')}">
          <h2 class="gg-welcome__team-heading">${this.t('welcome_team_heading')}</h2>
          <div class="gg-welcome__team-grid">${teamHtml}</div>
          <p class="gg-welcome__team-result">${this.t('welcome_team_result')}</p>
        </div>
      </div>
    `;
  }

  /* ---------- question dispatcher ---------- */
  renderQuestion() {
    const q = this.state.currentQ;
    const methods = {
      q1: () => this.renderTextQ('q1', 'q1_heading', 'q1_question', 'q1_placeholder', true),
      q3: () => this.renderSelectQ('q3',
        'q3_heading_full',
        'q3_question',
        [
          { val: 'self', label: 'q3_self_label', desc: 'q3_self_desc' },
          { val: 'her', label: 'q3_her_label', desc: 'q3_her_desc' },
          { val: 'him', label: 'q3_him_label', desc: 'q3_him_desc' },
        ]),
      q4: () => this.renderTextQ('q4', 'q4_heading', 'q4_question', 'q4_placeholder', true),
      q5: () => this.renderQ5(),
      q6: () => this.renderQ6(),
      q7: () => this.renderSelectQ('q7', 'q7_heading', 'q7_question', [
        { val: 'complete', label: 'q7_complete_label', desc: 'q7_complete_desc' },
        { val: 'stack', label: 'q7_stack_label', desc: 'q7_stack_desc' },
        { val: 'different', label: 'q7_different_label', desc: 'q7_different_desc' },
      ]),
      q8: () => this.renderQ8(),
      q8b: () => this.renderMultiQ('q8b', 'q8b_heading', 'q8b_question', [
        { val: 'mood_monday', label: 'q8b_monday' },
        { val: 'mood_business', label: 'q8b_business' },
        { val: 'mood_dinner', label: 'q8b_dinner' },
        { val: 'mood_brunch', label: 'q8b_brunch' },
        { val: 'mood_other', label: 'q8b_other_mood' },
        { val: 'other_brands', label: 'q8b_other_brands' },
      ]),
      q9: () => this.renderMultiQ('q9', 'q9_heading', 'q9_question', [
        { val: 'yellow', label: 'q9_yellow' },
        { val: 'white', label: 'q9_white' },
        { val: 'rose', label: 'q9_rose' },
        { val: 'titanium', label: 'q9_titanium' },
      ]),
      q10: () => this.renderSelectQ('q10', 'q10_heading', 'q10_question', [
        { val: 'heavy', label: 'q10_bold_label', desc: 'q10_bold_desc' },
        { val: 'accent', label: 'q10_subtle_label', desc: 'q10_subtle_desc' },
        { val: 'minimal', label: 'q10_none_label', desc: 'q10_none_desc' },
      ]),
      /* --- Him path questions --- */
      q5hc: () => this.renderSelectQ('q5hc', 'q5hc_heading', 'q5hc_question', [
        { val: 'mood', label: 'q5hc_mood_label', desc: 'q5hc_mood_desc' },
        { val: 'tryme', label: 'q5hc_tryme_label', desc: 'q5hc_tryme_desc' },
        { val: 'latch', label: 'q5hc_latch_label', desc: 'q5hc_latch_desc' },
        { val: 'other_brands', label: 'q5hc_other_label', desc: 'q5hc_other_desc' },
        { val: 'first', label: 'q5hc_first_label', desc: 'q5hc_first_desc' },
      ]),
      q6h: () => {
        const opts = [
          { val: 'daily', label: 'q6h_daily_label', desc: 'q6h_daily_desc' },
          { val: 'minimal', label: 'q6h_minimal_label', desc: 'q6h_minimal_desc' },
        ];
        // "First piece" is contradictory with "Building a collection"
        if (this.state.answers.q5 !== 'collection') {
          opts.push({ val: 'first', label: 'q6h_first_label', desc: 'q6h_first_desc' });
        }
        return this.renderSelectQ('q6h', 'q6h_heading', 'q6h_question', opts);
      },
      q7h: () => this.renderMultiQ('q7h', 'q7h_heading', 'q7h_question', [
        { val: 'rings', label: 'q7h_rings' },
        { val: 'bracelets', label: 'q7h_bracelets' },
        { val: 'necklaces', label: 'q7h_necklaces' },
        { val: 'watch_only', label: 'q7h_watch' },
        { val: 'nothing', label: 'q7h_nothing' },
      ]),
      q8h: () => this.renderSelectQ('q8h', 'q8h_heading', 'q8h_question', [
        { val: 'bold', label: 'q8h_bold_label', desc: 'q8h_bold_desc' },
        { val: 'understated', label: 'q8h_understated_label', desc: 'q8h_understated_desc' },
        { val: 'classic', label: 'q8h_classic_label', desc: 'q8h_classic_desc' },
      ]),
      q10h: () => this.renderSelectQ('q10h', 'q10h_heading', 'q10h_question', [
        { val: 'heavy', label: 'q10h_bold_label', desc: 'q10h_bold_desc' },
        { val: 'accent', label: 'q10h_subtle_label', desc: 'q10h_subtle_desc' },
        { val: 'minimal', label: 'q10h_none_label', desc: 'q10h_none_desc' },
      ]),
      /* --- End him path --- */
      q11: () => this.renderQ11(),
      q11b: () => this.renderQ11b(),
      q12: () => this.renderSelectQ('q12', 'q12_heading', 'q12_question', [
        { val: 'entry', label: 'q12_under3k_label', desc: 'q12_under3k_desc' },
        { val: 'mid', label: 'q12_3k6k_label', desc: 'q12_3k6k_desc' },
        { val: 'high', label: 'q12_6k10k_label', desc: 'q12_6k10k_desc' },
        { val: 'premium', label: 'q12_open_label', desc: 'q12_open_desc' },
      ]),
    };
    return methods[q] ? methods[q]() : '';
  }

  /* ---------- reusable question templates ---------- */
  backBtnHtml() {
    if (this.state.qNumber <= 0) return '';
    return `<button class="gg-back" data-action="back" aria-label="${this.t('back')}"><span aria-hidden="true">&larr;</span> ${this.t('back')}</button>`;
  }

  /* ---------- progress bar ----------

     Replaces the "{current} of {total}" text counter (Camille, 18/08/2026:
     "can we show a progress bar instead of the ~10?").

     estTotal() is an ESTIMATE: 8 on the For Him branch, 10 otherwise, and
     which branch the visitor is on is not known until q3 is answered. Two
     consequences are handled here rather than hoped away.

     1. qNumber can in principle reach or pass the estimate, so both the fill
        and aria-valuenow are clamped to [0, total]. The bar cannot exceed
        100% and cannot report a step that does not exist.

     2. The estimate only ever SHRINKS (10 to 8, when the visitor picks For
        Him), and shrinking the denominator raises the percentage. So a
        revised estimate can never make the bar run backwards. It recedes
        only when the visitor presses Back, which is the honest thing for it
        to do.

     The number is gone from the screen but not from assistive technology.
     aria-valuetext carries the step_counter string, which is exactly why
     that locale key is kept rather than deleted, and announceStep() still
     reads it out with the heading on every step. */
  progressHtml() {
    const total = this.estTotal();
    const current = Math.max(0, Math.min(this.state.qNumber, total));
    const percent = total > 0 ? Math.round((current / total) * 100) : 0;
    const valueText = this.t('step_counter', { current, total });

    /* Animate from wherever the bar already was, never from zero: the
       innerHTML replacement in render() destroys the old element, so without
       this the fill would restart at 0 on every step and read as a reset
       rather than as progress. Under prefers-reduced-motion the fill is
       rendered at its final width immediately, so there is no intermediate
       frame to see and no transition to suppress. */
    const from = typeof this._progressPercent === 'number' ? this._progressPercent : 0;
    const reduceMotion = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._progressPercent = percent;

    return `
      <div class="gg-progress" role="progressbar" aria-label="${escapeHtml(this.t('progress_label'))}" aria-valuemin="0" aria-valuemax="${total}" aria-valuenow="${current}" aria-valuetext="${escapeHtml(valueText)}">
        <span class="gg-progress__fill" style="width: ${reduceMotion ? percent : from}%" data-gg-progress-to="${percent}"></span>
      </div>
    `;
  }

  wrapQ(qId, headingKey, questionKey, content, showProgress = true) {
    const progress = showProgress && this.state.qNumber > 0 ? this.progressHtml() : '';

    return `
      <div class="gg-inner gg-question" role="region" aria-label="${escapeHtml(this.t('question_region', { number: this.state.qNumber }))}">
        ${progress}
        <h2 class="gg-question__heading">${this.tp(headingKey)}</h2>
        <p class="gg-question__label">${this.tp(questionKey)}</p>
        ${content}
      </div>
    `;
  }

  renderTextQ(qId, headingKey, questionKey, placeholderKey, skippable = false) {
    const skipBtn = skippable
      ? `<button class="gg-btn--skip gg-text-skip" data-action="skip" data-q="${qId}">${this.t(qId === 'q1' ? 'q1_skip' : 'q4_skip')}</button>`
      : '';
    // WCAG 3.3.2 — aria-label provides the input's programmatic accessible
    // name. A real <label for=> would be preferable in isolation, but
    // assets/floating-label.js hooks ANY label-to-input pair on the page: it
    // strips the label's `visually-hidden` class, adds `.floating-label`
    // (for which this theme defines no CSS at all — verified in the browser,
    // zero matching rules on the page), and removes the input's placeholder.
    // The result is the label rendered as plain visible text beside the field
    // with the placeholder gone. sections/footer-newsletter.liquid hit exactly
    // this and documents aria-label as the house solution; following it here
    // rather than inventing a third approach.
    // q1 = visitor's own first name → autocomplete="given-name" so password
    // managers / browser autofill help. q4 = recipient's first name →
    // autocomplete="off" because it isn't the visitor's own data.
    const inputId = `gg-text-${qId}`;
    const autocomplete = qId === 'q1' ? 'given-name' : 'off';
    const labelText = this.t(placeholderKey);
    const content = `
      <div class="gg-text-group">
        <input type="text" id="${inputId}" class="gg-text-input" data-q="${qId}" placeholder="${labelText}" aria-label="${labelText}" autocomplete="${autocomplete}" autocapitalize="words" />
        ${skipBtn}
        <div class="gg-actions">
          ${this.backBtnHtml()}
          <button class="gg-btn gg-btn--primary gg-btn--disabled" data-action="answer-text" data-q="${qId}" disabled aria-disabled="true">${this.t(qId === 'q1' ? 'q1_continue' : 'q4_continue')}</button>
        </div>
      </div>
    `;
    return this.wrapQ(qId, headingKey, questionKey, content, qId !== 'q1');
  }

  renderSelectQ(qId, headingKey, questionKey, options) {
    const optionsHtml = options.map(opt => `
      <button class="gg-option" data-action="answer" data-q="${qId}" data-val="${opt.val}">
        <span class="gg-option__label">${this.tp(opt.label)}</span>
        ${opt.desc ? `<span class="gg-option__desc">${this.tp(opt.desc)}</span>` : ''}
      </button>
    `).join('');

    const content = `
      <div class="gg-options">${optionsHtml}</div>
      <div class="gg-actions">${this.backBtnHtml()}</div>
    `;
    return this.wrapQ(qId, headingKey, questionKey, content);
  }

  renderMultiQ(qId, headingKey, questionKey, options) {
    const submitKeys = { q8b: 'q8b_continue', q7h: 'q7h_continue', q11b: 'q11b_continue' };
    const submitKey = submitKeys[qId] || 'q9_continue';
    // F-09 — split label + desc into separate elements with unique IDs so
    // the button's accessible name is the option label only (via
    // aria-labelledby) and the desc is exposed as aria-describedby. Without
    // this, AT reads "{label} {desc}" as one continuous accessible name,
    // muddying the role=checkbox announcement.
    const optionsHtml = options.map((opt, i) => {
      const labelId = `gg-opt-label-${qId}-${i}`;
      const descId = opt.desc ? `gg-opt-desc-${qId}-${i}` : '';
      const describedBy = descId ? ` aria-describedby="${descId}"` : '';
      return `
      <button class="gg-option gg-option--multi" data-action="multi-toggle" data-q="${qId}" data-val="${opt.val}" role="checkbox" aria-checked="false" aria-labelledby="${labelId}"${describedBy}>
        <span class="gg-checkbox" aria-hidden="true"></span>
        <span class="gg-option__text">
          <span class="gg-option__label" id="${labelId}">${this.tp(opt.label)}</span>
          ${opt.desc ? `<span class="gg-option__desc" id="${descId}">${this.tp(opt.desc)}</span>` : ''}
        </span>
      </button>
    `;
    }).join('');

    const content = `
      <div class="gg-options">${optionsHtml}</div>
      <div class="gg-actions">
        ${this.backBtnHtml()}
        <button class="gg-btn gg-btn--primary" data-action="multi-submit" data-q="${qId}">${this.t(submitKey)}</button>
      </div>
    `;
    return this.wrapQ(qId, headingKey, questionKey, content);
  }

  /* ---------- dynamic question builders ---------- */
  renderQ5() {
    const p = pronouns(this.state, document.documentElement.lang);
    const qKey = p.isSelf ? 'q5_question_self' : 'q5_question_gift';
    const options = p.isSelf
      ? [
          { val: 'selfcare', label: 'q5_self_celebrating_label', desc: 'q5_self_celebrating_desc' },
          { val: 'holiday', label: 'q5_self_birthday_label', desc: 'q5_self_birthday_desc' },
          { val: 'collection', label: 'q5_self_collection_label', desc: 'q5_self_collection_desc' },
        ]
      : [
          { val: 'milestone', label: 'q5_gift_milestone_label', desc: 'q5_gift_milestone_desc' },
          { val: 'holiday', label: 'q5_gift_birthday_label', desc: 'q5_gift_birthday_desc' },
          { val: 'collection', label: 'q5_gift_collection_label', desc: 'q5_gift_collection_desc' },
        ];
    return this.renderSelectQ('q5', 'q5_heading', qKey, options);
  }

  renderQ6() {
    return this.renderSelectQ('q6', 'q6_heading', 'q6_question', [
      { val: 'mood', label: 'q6_mood_label', desc: 'q6_mood_desc' },
      { val: 'tryme', label: 'q6_tryme_label', desc: 'q6_tryme_desc' },
      { val: 'latch', label: 'q6_latch_label', desc: 'q6_latch_desc' },
      { val: 'other_brands', label: 'q6_other_label', desc: 'q6_other_desc' },
      { val: 'first', label: 'q6_first_label', desc: 'q6_first_desc' },
    ]);
  }

  renderQ8() {
    return this.renderSelectQ('q8', 'q8_heading', 'q8_question', [
      { val: 'necklace', label: 'q8_necklace_label', desc: 'q8_necklace_desc' },
      { val: 'bracelet_stack', label: 'q8_bracelet_label', desc: 'q8_bracelet_desc' },
      { val: 'earrings', label: 'q8_earrings_label', desc: 'q8_earrings_desc' },
      { val: 'watch', label: 'q8_watch_label', desc: 'q8_watch_desc' },
      { val: 'none', label: 'q8_none_label', desc: 'q8_none_desc' },
    ]);
  }

  renderQ11() {
    return this.renderSelectQ('q11', 'q11_heading', 'q11_question', [
      { val: 'layers', label: 'q11_layers_label', desc: 'q11_layers_desc' },
      { val: 'signature', label: 'q11_statement_label', desc: 'q11_statement_desc' },
      { val: 'everyday', label: 'q11_everyday_label', desc: 'q11_everyday_desc' },
      { val: 'minimal_style', label: 'q11_minimal_label', desc: 'q11_minimal_desc' },
    ]);
  }

  renderQ11b() {
    const p = pronouns(this.state, document.documentElement.lang);
    const qKey = p.isSelf ? 'q11b_question_self' : 'q11b_question_gift';
    const suffix = p.isSelf ? '_self' : '_gift';
    const unknownVal = p.isSelf ? 'unknown_self' : 'unknown_gift';
    return this.renderMultiQ('q11b', 'q11b_heading', qKey, [
      { val: 'ring_yes', label: 'q11b_ring_label' + suffix },
      { val: 'wrist_yes', label: 'q11b_wrist_label' + suffix },
      { val: unknownVal, label: 'q11b_unsure_label' + suffix, desc: 'q11b_unsure_desc' + suffix },
    ]);
  }

  /* ---------- results ---------- */
  renderResults() {
    this._consentChecked = false;
    this.logEvent('results_shown', { answers: this.state.answers });

    /* One title. The express path was removed on 18/08/2026 (Camille: "we
       dont want an express path"), so there is nothing left to branch on. */
    const title = this.t('results_full_title');

    /* Real picks, chosen by scoring the answers against the 40 scenarios.
       Layout is driven by how many advisors actually covered the scenario,
       never hardcoded: one pick reads as a hero, two or three as a grid. */
    const match = chooseRecommendations(this.state);
    const recos = match.picks;

    const layout = recos.length === 1 ? 'hero' : 'grid';
    const gridClass = `gg-results__grid gg-results__grid--${layout}`;

    const cardsHtml = recos.map((pick) => {
      const advisor = TEAM[pick.advisor];
      const card = cardMarkup(pick.handle);

      /* If the card template is missing the product was unpublished
         between page render and now, which should not happen inside one
         page load. Skip rather than render a broken tile. */
      if (!card) return '';

      /* One attribution, once.

         The previous version printed "EMILY RECOMMENDS:" and then, directly
         beneath it, a grey panel repeating "Emily / VIP Specialist / note"
         next to a square holding the letter E. The same person was named
         three times in three different type styles, inside a filled box that
         appears nowhere else on the site. That is what made the results read
         as older than the rest of the storefront.

         Now: one quiet line naming who chose it, and their sentence. No box,
         no fill, no avatar. The product card does the visual work, exactly as
         it does on a collection page. */
      const attribution = advisor
        ? `<p class="gg-product__by">${advisor.name} <span class="gg-product__role">${this.t(advisor.roleKey)}</span></p>`
        : '';

      /* Still only when there is a real person AND a real note. No note means
         no quote, rather than a plausible-sounding line nobody said. */
      const why = advisor && pick.why
        ? `<p class="gg-product__why">${escapeHtml(pick.why)}</p>`
        : '';

      return `
        <div class="gg-product gg-product--${layout}">
          <div class="gg-product__card">${card}</div>
          <div class="gg-product__info">
            ${attribution}
            ${why}
            <a href="${pick.url}" class="gg-product__cta">${this.t('results_view_piece')} &rarr;</a>
          </div>
        </div>
      `;
    }).join('');

    // WCAG 3.3.2 — aria-label, for the floating-label.js reason documented on
    // renderTextQ above. The visible cue is the placeholder plus the
    // surrounding heading and subtext.
    // F-09 — for the consent buttons we expose only the consent text as the
    // accessible name (aria-labelledby) and keep the visual checkbox span
    // aria-hidden. Without this, AT was reading every consent line as one
    // continuous string with no separation between checkbox role and label.
    return `
      <div class="gg-inner gg-results" role="region" aria-label="${title}">
        <div class="gg-results__badge">${title}</div>
        <p class="gg-results__note">${this.personalNote()}</p>
        ${cardsHtml
          ? `<div class="${gridClass}">${cardsHtml}</div>`
          : `<p class="gg-results__empty">${this.t('results_empty')}</p>`}

        <div class="gg-save">
          <p class="gg-save__heading" id="gg-save-heading">${this.t('save_heading')}</p>
          <p class="gg-save__text" id="gg-save-subtext">${this.t('save_subtext')}</p>
          <div class="gg-save__form" role="group" aria-labelledby="gg-save-heading" aria-describedby="gg-save-subtext">
            <input
              type="email"
              id="gg-email-input"
              class="gg-email-input"
              placeholder="${this.t('save_email_placeholder')}"
              aria-label="${this.t('save_email_label')}"
              autocomplete="email"
              spellcheck="false"
              autocapitalize="off"
              aria-required="true"
              aria-describedby="gg-email-error"
            />
            <button class="gg-btn gg-btn--primary" data-action="save-email">${this.t('save_submit')}</button>
          </div>
          <p class="gg-save__error" id="gg-email-error" role="alert" aria-live="assertive" hidden></p>
          <div class="gg-save__consents">
            <!-- ONE checkbox, not two (Camille, 11/08/2026). Klaviyo skips a
                 flow email to a profile that never subscribed, so the optional
                 newsletter tick was the thing deciding whether the promised
                 recommendations arrived. Everyone who submits now subscribes,
                 which means the box has to SAY so. A second, redundant tick
                 would only be asking twice for the same permission. -->
            <button class="gg-consent" data-action="consent-toggle" data-field="consent" role="checkbox" aria-checked="false" aria-labelledby="gg-consent-text-storage">
              <span class="gg-checkbox" aria-hidden="true"></span>
              <span class="gg-consent__text" id="gg-consent-text-storage">${this.t('save_consent')}</span>
            </button>
          </div>
        </div>

        <div class="gg-results__actions">
          <a href="${(window.NH_ROUTES && window.NH_ROUTES.collections) || '/collections'}" class="gg-btn gg-btn--primary">${this.t('cta_explore')}</a>
          <button class="gg-btn--skip" data-action="restart">${this.t('cta_start_over')}</button>
        </div>
      </div>
    `;
  }
}

/* ============================================================
   INPUT WATCHER: enable/disable continue button for text inputs.
   WCAG 4.1.2 — keep the disabled attribute and aria-disabled in sync
   with the .gg-btn--disabled class so AT announces state correctly
   and keyboard users cannot activate the button while empty.
   ============================================================ */
document.addEventListener('input', (e) => {
  if (!e.target.classList.contains('gg-text-input')) return;
  const group = e.target.closest('.gg-text-group');
  if (!group) return;
  const btn = group.querySelector('.gg-btn--primary');
  if (!btn) return;
  const isDisabled = !e.target.value.trim();
  btn.classList.toggle('gg-btn--disabled', isDisabled);
  btn.disabled = isDisabled;
  btn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
});

if (!customElements.get('gift-guide-quiz')) {
  customElements.define('gift-guide-quiz', GiftGuideQuiz);
}